import {
  cardTitleTextClass,
  secondaryBtnClass,
} from "@/components/common/surfaceClasses";
import type { ReportingForestNode } from "./adminContactsDirectoryShared";

type NodeInfo = {
  name: string;
  title: string;
  source: "user" | "external";
};

type Props = {
  reportingForest: ReportingForestNode[];
  reportingTreeViewMode: "org_chart" | "stacked";
  collapsedReportingKeys: string[];
  onToggleCollapsed: (key: string) => void;
  reportingNodeLabel: (key: string) => NodeInfo;
};

function ReportingChartNode({
  node,
  depth,
  collapsedReportingKeys,
  onToggleCollapsed,
  reportingNodeLabel,
}: {
  node: ReportingForestNode;
  depth: number;
  collapsedReportingKeys: string[];
  onToggleCollapsed: (key: string) => void;
  reportingNodeLabel: (key: string) => NodeInfo;
}) {
  const info = reportingNodeLabel(node.key);
  const indentPx = depth * 24;
  const hasChildren = node.children.length > 0;
  const isCollapsed = collapsedReportingKeys.includes(node.key);
  return (
    <div key={node.key} className={depth > 0 ? "mt-1.5" : ""} style={{ paddingLeft: `${indentPx}px` }}>
      <div className={`rounded-md border bg-white px-2 py-1.5 text-xs dark:bg-zinc-900/50 ${depth === 0 ? "border-gray-300 dark:border-zinc-600" : "border-gray-200 dark:border-zinc-700"}`}>
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[10px] text-gray-600 dark:border-zinc-700 dark:bg-zinc-800/60 dark:text-zinc-400">
            L{depth}
          </span>
          {hasChildren ? (
            <button
              type="button"
              className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] ${secondaryBtnClass}`}
              onClick={() => onToggleCollapsed(node.key)}
              title={isCollapsed ? "Expand node" : "Collapse node"}
            >
              {isCollapsed ? "Expand" : "Collapse"}
            </button>
          ) : null}
          <span className={`font-medium ${cardTitleTextClass}`}>{info.name}</span>
          <span
            className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] border ${
              info.source === "user"
                ? "border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-300"
                : "border-gray-200 bg-gray-50 text-gray-600 dark:border-zinc-700 dark:bg-zinc-800/60 dark:text-zinc-400"
            }`}
          >
            {info.source === "user" ? "User" : "External"}
          </span>
          {info.title ? <span className="text-gray-500 dark:text-zinc-400">{info.title}</span> : null}
        </div>
      </div>
      {hasChildren && !isCollapsed ? (
        <div className="mt-1 space-y-1">
          {node.children.map((child) => (
            <ReportingChartNode
              key={child.key}
              node={child}
              depth={depth + 1}
              collapsedReportingKeys={collapsedReportingKeys}
              onToggleCollapsed={onToggleCollapsed}
              reportingNodeLabel={reportingNodeLabel}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function ReportingStackedNode({
  node,
  depth,
  reportingNodeLabel,
}: {
  node: ReportingForestNode;
  depth: number;
  reportingNodeLabel: (key: string) => NodeInfo;
}) {
  const info = reportingNodeLabel(node.key);
  const prefix = Array.from({ length: depth }, () => "|").join(" ");
  return (
    <div key={node.key} className="space-y-1">
      <div className="font-mono text-[11px] leading-snug text-gray-800 dark:text-zinc-300">
        {prefix ? `${prefix} ` : ""}
        {info.name}
        {info.title ? ` | ${info.title}` : ""}
        {info.source === "user" ? " | user" : " | external"}
      </div>
      {node.children.length ? (
        <div className="ml-3">
          {node.children.map((child) => (
            <ReportingStackedNode key={child.key} node={child} depth={depth + 1} reportingNodeLabel={reportingNodeLabel} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function AdminContactsReportingTreeView({
  reportingForest,
  reportingTreeViewMode,
  collapsedReportingKeys,
  onToggleCollapsed,
  reportingNodeLabel,
}: Props) {
  if (!reportingForest.length) {
    return <p className="text-xs text-gray-400 dark:text-zinc-500">No reporting nodes found for this organization.</p>;
  }
  if (reportingTreeViewMode === "org_chart") {
    return (
      <div className="space-y-2">
        {reportingForest.map((root) => (
          <ReportingChartNode
            key={root.key}
            node={root}
            depth={0}
            collapsedReportingKeys={collapsedReportingKeys}
            onToggleCollapsed={onToggleCollapsed}
            reportingNodeLabel={reportingNodeLabel}
          />
        ))}
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {reportingForest.map((root) => (
        <ReportingStackedNode key={root.key} node={root} depth={0} reportingNodeLabel={reportingNodeLabel} />
      ))}
    </div>
  );
}
